import type { BillingHistoryDoc } from "../Services/Billing";
import { getOfficeBillingHistory } from "../Services/Billing";
import { getFirmById, getOfficesByFirmId } from "../Services/Firm";
import { membershipDuesFiscalYearForOffice } from "./billingYear";
import { todayIso } from "./dates";
import { isCancelledBillingDoc } from "./billingStatus";
import { isAnnualInvoice } from "./billingLedgerUtils";

export type FirmStatementBillingContext = {
  docs: BillingHistoryDoc[];
  /** Membership-dues fiscal year per office (same rule as Create Invoice). */
  currentFiscalYearByOfficeId: Map<string, number>;
  /** office.balance per office (positive = debt, negative = credit). */
  officeBalanceByOfficeId: Map<string, number>;
};

export type FirmAccountStatement = {
  totalPaymentReceived: number;
  duesInvoiced: number;
  totalBalance: number;
  duesBalanceCurrentFiscalYear: number;
  invoiceMeetingBalanceCurrentFiscalYear: number;
};

type StatementInvoiceItem = NonNullable<BillingHistoryDoc["items"]>[number];

const DUES_BALANCE_ITEM_CODES = new Set(["annual", "special", "discount"]);
const MEETING_PREPAID_ITEM_CODE = "annualMeeting";

function itemLineAmount(item: StatementInvoiceItem): number {
  return (Number(item.amount) || 0) * (Number(item.quantity) || 1);
}

function itemPaidAmount(
  item: StatementInvoiceItem,
  invoice: BillingHistoryDoc,
): number {
  const items = invoice.items ?? [];
  const lineAmount = itemLineAmount(item);
  const hasItemPaid = items.some((entry) => entry.paid != null);
  if (hasItemPaid) {
    return Number(item.paid) || 0;
  }

  const invoiceTotal = Number(invoice.total) || 0;
  const invoicePaid = Number(invoice.totalPaid) || 0;
  if (invoiceTotal <= 0) {
    return 0;
  }
  return invoicePaid * (lineAmount / invoiceTotal);
}

function itemOutstandingBalance(
  item: StatementInvoiceItem,
  invoice: BillingHistoryDoc,
): number {
  return itemLineAmount(item) - itemPaidAmount(item, invoice);
}

function sumItemOutstandingByCodes(
  invoice: BillingHistoryDoc,
  codes: Set<string>,
): number {
  const items = invoice.items ?? [];
  if (items.length === 0) {
    return 0;
  }

  let sum = 0;
  for (const item of items) {
    const code = String(item.code ?? "").trim();
    if (!codes.has(code)) {
      continue;
    }
    sum += itemOutstandingBalance(item, invoice);
  }
  return sum;
}

function resolveOfficeIdFromDoc(doc: BillingHistoryDoc): string | null {
  const partyId = String(doc.partyId ?? "").trim();
  if (partyId) {
    return partyId;
  }
  const partyKey = String(doc.partyKey ?? "").trim();
  if (partyKey.startsWith("office:")) {
    return partyKey.slice("office:".length).trim() || null;
  }
  return null;
}

function isMembershipDuesInvoiceForOfficeCurrentFiscalYear(
  doc: BillingHistoryDoc,
  currentFiscalYearByOfficeId: Map<string, number>,
): boolean {
  if (!isAnnualInvoice(doc)) {
    return false;
  }
  const officeId = resolveOfficeIdFromDoc(doc);
  if (!officeId) {
    return false;
  }
  const expectedYear = currentFiscalYearByOfficeId.get(officeId);
  if (expectedYear === undefined) {
    return false;
  }
  const docYear = Number(doc.context?.fiscalYear);
  return Number.isFinite(docYear) && docYear === expectedYear;
}

export function computeFirmAccountStatement(
  docs: BillingHistoryDoc[],
  currentFiscalYearByOfficeId: Map<string, number>,
  officeBalanceByOfficeId: Map<string, number> = new Map(),
): FirmAccountStatement {
  let totalPaymentReceived = 0;
  let duesInvoiced = 0;
  let totalBalance = 0;
  let duesBalanceCurrentFiscalYear = 0;
  let invoiceMeetingBalanceCurrentFiscalYear = 0;

  for (const doc of docs) {
    if (isCancelledBillingDoc(doc)) {
      continue;
    }

    if (doc.type === "payment") {
      totalPaymentReceived += Number(doc.amount) || 0;
      continue;
    }

    const invoiceTotal = Number(doc.total) || 0;
    if (isAnnualInvoice(doc)) {
      duesInvoiced += invoiceTotal;
    }

    const balance = Number(doc.balance);
    if (Number.isFinite(balance)) {
      totalBalance += balance;
    } else {
      const paid = Number(doc.totalPaid) || 0;
      totalBalance += Math.max(0, invoiceTotal - paid);
    }

    if (!isMembershipDuesInvoiceForOfficeCurrentFiscalYear(doc, currentFiscalYearByOfficeId)) {
      continue;
    }

    duesBalanceCurrentFiscalYear += sumItemOutstandingByCodes(
      doc,
      DUES_BALANCE_ITEM_CODES,
    );
    invoiceMeetingBalanceCurrentFiscalYear += sumItemOutstandingByCodes(
      doc,
      new Set([MEETING_PREPAID_ITEM_CODE]),
    );
  }

  for (const balance of officeBalanceByOfficeId.values()) {
    duesBalanceCurrentFiscalYear += Number(balance) || 0;
  }

  return {
    totalPaymentReceived: roundMoney(totalPaymentReceived),
    duesInvoiced: roundMoney(duesInvoiced),
    totalBalance: roundMoney(totalBalance),
    duesBalanceCurrentFiscalYear: roundMoney(duesBalanceCurrentFiscalYear),
    invoiceMeetingBalanceCurrentFiscalYear: roundMoney(
      invoiceMeetingBalanceCurrentFiscalYear,
    ),
  };
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export async function loadFirmStatementBillingContext(
  firmId: string,
  referenceDateIso: string = todayIso(),
): Promise<FirmStatementBillingContext> {
  const trimmedFirmId = firmId.trim();
  if (!trimmedFirmId) {
    return {
      docs: [],
      currentFiscalYearByOfficeId: new Map(),
      officeBalanceByOfficeId: new Map(),
    };
  }

  const [offices, firm] = await Promise.all([
    getOfficesByFirmId(trimmedFirmId),
    getFirmById(trimmedFirmId).catch(() => null),
  ]);

  const currentFiscalYearByOfficeId = new Map<string, number>();
  const officeBalanceByOfficeId = new Map<string, number>();
  const officeIds: string[] = [];

  for (const office of offices) {
    const officeId = String(office.id ?? "").trim();
    if (!officeId) {
      continue;
    }
    officeIds.push(officeId);
    officeBalanceByOfficeId.set(officeId, Number(office.balance) || 0);
    const billingStartDate =
      office.billingStartDate ?? firm?.billingStartDate ?? undefined;
    currentFiscalYearByOfficeId.set(
      officeId,
      membershipDuesFiscalYearForOffice(referenceDateIso, billingStartDate),
    );
  }

  if (officeIds.length === 0) {
    return {
      docs: [],
      currentFiscalYearByOfficeId,
      officeBalanceByOfficeId,
    };
  }

  const histories = await Promise.all(
    officeIds.map((officeId) => getOfficeBillingHistory(officeId)),
  );

  const byId = new Map<string, BillingHistoryDoc>();
  for (const docs of histories) {
    for (const doc of docs) {
      if (doc.id) {
        byId.set(doc.id, doc);
      }
    }
  }

  return {
    docs: [...byId.values()],
    currentFiscalYearByOfficeId,
    officeBalanceByOfficeId,
  };
}

/** @deprecated Use loadFirmStatementBillingContext */
export async function fetchFirmBillingHistoryForStatement(
  firmId: string,
): Promise<BillingHistoryDoc[]> {
  const context = await loadFirmStatementBillingContext(firmId);
  return context.docs;
}
